import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Unit testing is for lazy people",
  "description": "",
  "published": "2012-02-27T00:00:00.000Z",
  "redirect_from": ["/blog/unit-testing-is-for-lazy-people/swizec/3752"],
  "categories": "Debugging, Integration testing, Programming, Test, Test automation, TestUnit, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`The other day I was talking to a guy about a possible freelancing gig and he said how wonderful it was that I should bring up the topic of unit/`}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Test_automation",
        "title": "Test automation"
      }}>{`automated testing`}</a>{`without being asked. Said that most (many?) developers don't have the level of rigor to use automated testing.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/02/fallen-building.jpg",
        "alt": "Oops building",
        "title": "Oops building"
      }}></img></p>
    <p>{`My reaction was one of disbelief `}<em parentName="p">{`"Rigor!? But automated testing is one of the laziest things a developer can do! It speeds stuff up `}{`_`}{`so`}</em>{` much!"`}{`_`}</p>
    <p>{`As luck would have it, last night I was hit over the head with my own words and nearly died `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Debugging",
        "title": "Debugging"
      }}>{`debugging`}</a>{` a single function.`}</p>
    <p>{`I was working on Stripe webhooks and for security reasons decided not to use the event data sent in request body. Makes sense right? Take the event id from request body, then fetch the actual event from Stripe.`}</p>
    <p>{`It's the only way to be certain you aren't responding to bogus events sent by an evil person trying to make you look bad (nothing `}<em parentName="p">{`actually`}</em>{` bad can happen, at worst a customer would get extra paid invoice emails).`}</p>
    <p>{`Due to poor decoupling - I didn't really want to decouple a 6 line function into two functions - everything was now difficult to test. I can't create events on Stripe's servers with `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Unit_testing",
        "title": "Unit testing"
      }}>{`unit tests`}</a>{` and without actual events existing I can't test the function works as it's supposed to.`}</p>
    <p>{`How many bugs can you put in a 6 line function anyway?`}</p>
    <h2 {...{
      "id": "a-lot-of-bugs"
    }}>{`A lot of bugs!`}</h2>
    <p>{`When the client tested on staging ... it didn't work. Invoice email wasn't sent and Stripe complained of a `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/List_of_HTTP_status_codes",
        "title": "List of HTTP status codes"
      }}>{`500 error`}</a>{`.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/02/300px-Army_Camp-sml.jpg",
        "alt": "English: British Army Signals Trials Unit test...",
        "title": "English: British Army Signals Trials Unit test..."
      }}></img></p>
    <p>{`It took me almost two hours to fix all the bugs because my testing cycle looked like this:`}</p>
    <ol>
      <li parentName="ol">{`change code`}</li>
      <li parentName="ol">{`commit to `}<em parentName="li">{`develop branch`}</em></li>
      <li parentName="ol">{`switch to `}<em parentName="li">{`staging branch`}</em></li>
      <li parentName="ol">{`merge develop branch into staging`}</li>
      <li parentName="ol">{`push to github`}</li>
      <li parentName="ol">{`change to other terminal window`}</li>
      <li parentName="ol">{`pull from staging branch`}</li>
      <li parentName="ol">{`restart python processes`}</li>
      <li parentName="ol">{`go to Stripe dashboard`}</li>
      <li parentName="ol">{`pick customer`}</li>
      <li parentName="ol">{`create invoice item`}</li>
      <li parentName="ol">{`create actual invoice`}</li>
      <li parentName="ol">{`choose invoice`}</li>
      <li parentName="ol"><em parentName="li">{`pay`}</em>{` invoice`}</li>
      <li parentName="ol">{`go to Stripe logs`}</li>
      <li parentName="ol">{`find `}<em parentName="li">{`invoice.payment_succeeded`}</em>{` webhook`}</li>
      <li parentName="ol">{`scroll down to response`}</li>
      <li parentName="ol">{`look through raw html of django's error page`}</li>
      <li parentName="ol">{`find symptom`}</li>
      <li parentName="ol">{`GOTO 1.`}</li>
    </ol>
    <p>{`That's right, a whopping `}<em parentName="p">{`20 step`}</em>{` debug cycle all because I'm an idiot and couldn't find a way to automate this. Or maybe I was too tired to do the unobvious thing ... although I still don't want to split a 6 liner into two functions.`}</p>
    <p>{`With proper `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Unit_testing",
        "title": "Unit testing"
      }}>{`unit testing`}</a>{` the debug cycle would look like this:`}</p>
    <ol>
      <li parentName="ol">{`change code`}</li>
      <li parentName="ol">{`run tests`}</li>
      <li parentName="ol">{`symptom thrown in face`}</li>
      <li parentName="ol">{`GOTO 1.`}</li>
    </ol>
    <p>{`Much lazier right?`}</p>
    <p>{`For the record, those six lines of code contained `}<strong parentName="p">{`4`}</strong>{` bugs ->`}</p>
    <ul>
      <li parentName="ul">{`forgot to import a module`}</li>
      <li parentName="ul">{`different event data structure than I understood from the docs`}</li>
      <li parentName="ul">{`twice^`}</li>
      <li parentName="ul">{`misnamed variables from one line to another`}</li>
    </ul>
    <p>{`Yes, all of those could `}<em parentName="p">{`easily`}</em>{` have been caught if my `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Fault_coverage",
        "title": "Fault coverage"
      }}>{`test coverage`}</a>{` was actually any good! And then not only would I not look like an idiot in front of the client, I'd probably spend no more than ten minutes fixing this.`}</p>
    <p>{`Let this be a lesson both to you and to Future Swizec!`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/9418892/easymock-times-behaviour-not-being-checked-for-strict-mock"
        }}>{`EasyMock: Times behaviour not being checked for strict mock`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/8719147/how-do-continuous-integration-and-automated-tests-look-in-practice"
        }}>{`How does continuous integration and automated tests look in practice?`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://autismjungle.wordpress.com/2012/02/26/change-of-plan/"
        }}>{`Change of Plan`}</a>{` (autismjungle.wordpress.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.makinggoodsoftware.com/2012/01/27/the-evil-unit-test/"
        }}>{`The evil unit test.`}</a>{` (makinggoodsoftware.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://kimphanvn.wordpress.com/2012/02/27/test-driven-development-best-practices-and-benefits-of-using-unit-testing-on-real-life-projects/"
        }}>{`Test Driven Development: Best Practices and Benefits of Using Unit Testing on Real Life Projects`}</a>{` (kimphanvn.wordpress.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://mallibone.wordpress.com/2012/02/14/run-your-tests-with-a-single-command/"
        }}>{`Run your all your tests with rake`}</a>{` (mallibone.wordpress.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.slideshare.net/gilzilberfeld/introduction-to-unit-testing-10983708"
        }}>{`Introduction to unit testing`}</a>{` (slideshare.net)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=140931a7-b24e-4c17-95ae-a9e183cc803b",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      